import React from "react";
import linkArrowIcon from "../assets/images/link-arrow.svg";
import { SanityNode } from "../config/build/models/node";
import useTranslations from "../hooks/useTranslations";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";
import { iconsList } from "../utilities/iconsList";
import { PortableTextColorThemeProps } from "./PortableText/PortableText";
import SanityLink from "./SanityLink";

export interface IconTextBlockProps {
  title?: string;
  subtitle?: string;
  preamble?: string;
  iconTextCards?: {
    _key: string;
    title: string;
    link: SanityNode;
    icon: string;
    text: string;
  }[];
  portableTextColorThemeProps?: PortableTextColorThemeProps;
}

const IconTextBlock = (props: IconTextBlockProps) => {
  const { title, subtitle, preamble, iconTextCards, portableTextColorThemeProps } = props;

  const translations = useTranslations();

  const {
    elementClasses: { h3Subheader: subtitleClasses = "text-subheader-primary" } = {}
  } = portableTextColorThemeProps || {};

  const LinkOrDiv = ({ children, iconCard, className }) => {
    const Wrapper = iconCard.link ? SanityLink : "div";
    return (
      <Wrapper reference={iconCard.link} className={className}>
        {children}
      </Wrapper>
    );
  };

  return (
    <aside className="my-[3.75rem] md:my-[5rem] xl:my-[8rem]">
      <div>
        {!isNullOrWhiteSpace(title) && <h2 className="text-current">{title}</h2>}
        {!isNullOrWhiteSpace(subtitle) && (
          <h3
            className={`${subtitleClasses} lg:mt-2 text-base lg:text-xl uppercase font-bold`}
          >
            {replaceWithUnicode(subtitle)}
          </h3>
        )}
        {!isNullOrWhiteSpace(preamble) && (
          <p className="text-lg md:text-2xl leading-9">{preamble}</p>
        )}
      </div>
      <div className="flex justify-center mt-8">
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 py-8 md:w-full">
          {iconTextCards?.map(iconCard => (
            <li key={iconCard._key} className="flex justify-center md:justify-start">
              <LinkOrDiv
                iconCard={iconCard}
                className="no-underline text-current outline-none flex flex-col focus:outline-none focus:shadow-card-outline rounded-md p-2.5 items-center md:items-start group"
              >
                <span className="p-8 bg-brand-primary-95 rounded-full text-brand-primary-20 flex h-icon-text-block w-icon-text-block justify-center items-center group-hover:bg-icon-text-hover group-hover:text-neutral-100 transform transition-all duration-200">
                  <svg width={60} height={60} className="fill-current order-1">
                    <use xlinkHref={iconsList[iconCard.icon]} />
                  </svg>
                </span>

                {!isNullOrWhiteSpace(iconCard.title) && (
                  <h3 className="font-bold text-3.5xl mt-7 uppercase text-current mb-2">
                    {replaceWithUnicode(iconCard.title)}
                  </h3>
                )}

                {!isNullOrWhiteSpace(iconCard.text) && (
                  <p className="text-current mt-2 mb-0">{iconCard.text}</p>
                )}

                {!!iconCard.link && (
                  <div className="flex items-center justify-start w-full mx-auto mt-6">
                    <span className="flex border-b text-brand-primary-50 border-brand-primary-70 font-bold no-underline focus:outline-none focus:shadow-card-outline outline-none">
                      <span>{translations.readMore ?? ""}</span>
                      <svg className="self-center card-arrow group-hover:translate-x-1 ml-2 w-4 h-4">
                        <use xlinkHref={linkArrowIcon.url} />
                      </svg>
                    </span>
                  </div>
                )}
              </LinkOrDiv>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default IconTextBlock;
